/* eslint-disable react/require-default-props */
import React from 'react';
import proptypes from 'prop-types';
const { bool, shape, func } = proptypes;

import { extend } from '@thd-nucleus/data-sources';

import { BundleMiniPlaceholder } from '../../BundleMiniPlaceholder';
import { LaundryBundleMiniContent } from '../../LaundryBundleMiniContent';
import { useLaundryEntryPoint } from '../../../hooks/useLaundryEntryPoint';
import { useMiniItemGate } from '../../../hooks/useMiniItemGate';
import { BundleProductsDataModel } from '../../../models/dataModel';

/**
 * BundleLaundryMiniItem is a component that renders a mini item
 * for a laundry entry point. It handles loading states, error handling, and rendering
 * the appropriate content based on the provided data.
 *
 * @param {Object} props - The props object.
 * @param {boolean} props.bundleLoading - Indicates whether the bundle data is loading.
 * @param {Object} props.bundleData - The data for the laundry bundle.
 * @param {Function} props.onError - Callback function to handle errors when rendering fails.
 *
 * @returns {JSX.Element|null} The rendered mini item content, a placeholder during loading,
 * or null if rendering conditions are not met.
 */
export const BundleLaundryMiniItem = ({ bundleLoading = false, bundleData = {}, onError = () => { } }) => {
  const {
    products, accessories, loading: productsLoading, error, isBundleModified, swapProduct, swapAccessory
  } = useLaundryEntryPoint({ bundle: bundleData });

  const { loading, renderMiniItem } = useMiniItemGate({
    bundleData, products, productsLoading, bundleLoading, error, onError
  });

  if (loading) {
    return <BundleMiniPlaceholder />;
  }

  if (!renderMiniItem) {
    return null;
  }

  return (
    <LaundryBundleMiniContent
      products={products}
      accessories={accessories}
      onSwapProduct={swapProduct}
      onSwapAccessory={swapAccessory}
      bundle={bundleData}
      isBundleModified={isBundleModified}
    />
  );
};

BundleLaundryMiniItem.propTypes = {
  bundleLoading: bool,
  bundleData: shape({}),
  onError: func
};

BundleLaundryMiniItem.dataModel = extend(
  BundleProductsDataModel,
  LaundryBundleMiniContent
);